import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CalendarClock } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { supplyService } from '@/services/supplyService';

/** Sin valor guardado, el backend aplica este %. */
const DEFAULT_URGENT_LEAD_TIME_PCT = 50;

export function SupplyDeliveryNeedDateSettingsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role?.id === 1;
  const [pctDraft, setPctDraft] = useState('');
  const [pctError, setPctError] = useState(false);

  const settings = useQuery({
    queryKey: ['supply-organization-settings'],
    queryFn: () => supplyService.getOrganizationSettings(),
  });

  const patch = useMutation({
    mutationFn: (payload: { delivery_date_use_need_date?: boolean; urgent_lead_time_pct?: number | null }) =>
      supplyService.updateOrganizationSettings(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['supply-organization-settings'] });
    },
  });

  const enabled = settings.data?.delivery_date_use_need_date ?? false;
  const savedPct = settings.data?.urgent_lead_time_pct;
  const effectivePct = typeof savedPct === 'number' ? savedPct : DEFAULT_URGENT_LEAD_TIME_PCT;

  useEffect(() => {
    setPctDraft(typeof savedPct === 'number' ? String(savedPct) : '');
  }, [savedPct]);

  const savePct = () => {
    const trimmed = pctDraft.trim();
    if (trimmed === '') {
      setPctError(false);
      patch.mutate({ urgent_lead_time_pct: null });
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed < 0 || parsed > 100) {
      setPctError(true);
      return;
    }
    setPctError(false);
    patch.mutate({ urgent_lead_time_pct: parsed });
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <CalendarClock className="w-5 h-5 text-stocker-primary" />
          {t('settings.supplyDeliveryDateNeed.title')}
        </CardTitle>
        <CardDescription>{t('settings.supplyDeliveryDateNeed.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {settings.isError ? (
          <p className="text-sm text-red-600">{t('settings.saveError')}</p>
        ) : settings.isLoading ? (
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        ) : (
          <div className="space-y-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                checked={enabled}
                disabled={!canEdit || patch.isPending}
                onChange={(e) => patch.mutate({ delivery_date_use_need_date: e.target.checked })}
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">
                  {t('settings.supplyDeliveryDateNeed.label')}
                </span>
                <span className="block text-xs text-gray-500">
                  {enabled
                    ? t('settings.supplyDeliveryDateNeed.onHint', { pct: effectivePct })
                    : t('settings.supplyDeliveryDateNeed.offHint')}
                </span>
              </span>
            </label>

            <div className="flex flex-wrap items-end gap-3">
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-800">
                  {t('settings.supplyDeliveryDateNeed.pctLabel')}
                </span>
                <span className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    inputMode="numeric"
                    value={pctDraft}
                    disabled={!canEdit || patch.isPending}
                    onChange={(e) => {
                      setPctDraft(e.target.value);
                      setPctError(false);
                    }}
                    placeholder={String(DEFAULT_URGENT_LEAD_TIME_PCT)}
                    className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  <span className="text-sm text-gray-600">%</span>
                </span>
              </label>
              {canEdit ? (
                <button
                  type="button"
                  onClick={savePct}
                  disabled={patch.isPending}
                  className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:bg-stocker-primary/90 disabled:opacity-50"
                >
                  {t('settings.supplyDeliveryDateNeed.pctSave')}
                </button>
              ) : null}
            </div>
            <p className="text-xs text-gray-500">
              {t('settings.supplyDeliveryDateNeed.pctHint', { pct: DEFAULT_URGENT_LEAD_TIME_PCT })}
            </p>
          </div>
        )}
        {pctError ? (
          <p className="mt-2 text-xs text-red-600">{t('settings.supplyDeliveryDateNeed.pctInvalid')}</p>
        ) : null}
        {!canEdit ? (
          <p className="mt-2 text-xs text-gray-500">{t('settings.supplyDeliveryDateNeed.readOnly')}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
